import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API, setAuthToken } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const AuthPage = () => {
  const { role } = useParams();
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [resetMode, setResetMode] = useState(false); // password reset flow
  const [resetStep, setResetStep] = useState(1); // 1=enter phone, 2=enter OTP+new password
  const [resetPhone, setResetPhone] = useState('');
  const [resetOTP, setResetOTP] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [mockOTP, setMockOTP] = useState('');
  
  const [formData, setFormData] = useState({
    phone: '',
    password: '',
    name: '',
    flat_number: '',
    otp: '',
    parking_slots: ['']
  });

  const handlePasswordResetRequest = async () => {
    if (!resetPhone || resetPhone.length < 10) {
      toast.error('Enter a valid 10-digit phone number');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/password-reset/request`, { phone: resetPhone });
      setMockOTP(res.data.otp);
      toast.success(`OTP sent: ${res.data.otp} (mock)`);
      setResetStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally { setLoading(false); }
  };

  const handlePasswordResetConfirm = async () => {
    if (!resetOTP || !resetPassword) {
      toast.error('Enter OTP and new password');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset/confirm`, { phone: resetPhone, otp: resetOTP, new_password: resetPassword });
      toast.success('Password reset successfully! Please login.');
      setResetMode(false);
      setResetStep(1);
      setResetPhone('');
      setResetOTP('');
      setResetPassword('');
      setMockOTP('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (role === 'visitor') {
        // Visitor OTP flow
        if (!formData.otp) {
          // Send OTP
          const otpRes = await axios.post(`${API}/auth/send-otp`, {
            phone: formData.phone
          });
          toast.success(`OTP sent: ${otpRes.data.otp}`);
          setLoading(false);
          return;
        } else {
          // Verify OTP
          const verifyRes = await axios.post(`${API}/auth/verify-otp`, {
            phone: formData.phone,
            otp: formData.otp
          });
          localStorage.setItem('role', 'visitor');
          toast.success('Authenticated successfully');
          // Visitor will be redirected by guard after booking
          navigate('/');
        }
      } else {
        // Owner/Guard/Admin flow
        if (isLogin) {
          const res = await axios.post(`${API}/auth/login`, {
            phone: formData.phone,
            password: formData.password
          });
          const userRole = res.data.user.role;
          // Enforce role match: admin page allows both admin and superadmin
          const allowedRoles = role === 'admin' ? ['admin', 'superadmin'] : [role];
          if (!allowedRoles.includes(userRole)) {
            toast.error(`This login is for ${role}s only. Your account is registered as ${userRole}.`);
            setLoading(false);
            return;
          }
          localStorage.setItem('role', userRole);
          setAuthToken(res.data.token);
          toast.success('Login successful');
          const redirectRole = userRole === 'superadmin' ? 'superadmin' : userRole;
          navigate(`/${redirectRole}`);
        } else {
          const registrationData = {
            phone: formData.phone,
            password: formData.password,
            name: formData.name,
            flat_number: formData.flat_number,
            role: role
          };
          
          // Add parking slots for owners
          if (role === 'owner') {
            registrationData.parking_slots = formData.parking_slots.filter(s => s.trim() !== '');
            if (registrationData.parking_slots.length === 0) {
              toast.error('Please add at least one parking slot');
              setLoading(false);
              return;
            }
          }
          
          const res = await axios.post(`${API}/auth/register`, registrationData);
          localStorage.setItem('role', res.data.user.role);
          setAuthToken(res.data.token);
          toast.success('Registration successful');
          navigate(`/${res.data.user.role}`);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const getPageTitle = () => {
    if (role === 'visitor') return 'Visitor Authentication';
    return isLogin ? 'Login' : 'Register';
  };

  const getSubmitLabel = () => {
    if (loading) return 'Processing...';
    if (role === 'visitor') return formData.otp ? 'Verify OTP' : 'Send OTP';
    return isLogin ? 'Login' : 'Register';
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: '#F4F4F5' }}>
      <div className="w-full max-w-md p-8 border" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7', borderWidth: '1px' }}>
        {resetMode ? (
          <>
            <h1 className="text-2xl sm:text-3xl tracking-tight font-bold mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }} data-testid="reset-title">
              Reset Password
            </h1>
            <p className="text-xs tracking-[0.2em] uppercase font-bold mb-8" style={{ color: '#71717A', fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {role.toUpperCase()}
            </p>

            {resetStep === 1 ? (
              <div className="space-y-6">
                <div>
                  <Label htmlFor="reset-phone" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#09090B' }}>Phone Number</Label>
                  <Input id="reset-phone" type="tel" placeholder="9876543210" value={resetPhone} onChange={(e) => setResetPhone(e.target.value)} data-testid="reset-phone-input" className="mt-2 p-4 border-2 focus:border-black" style={{ fontFamily: 'JetBrains Mono, monospace' }} />
                </div>
                <Button onClick={handlePasswordResetRequest} disabled={loading} data-testid="send-reset-otp-btn" className="w-full p-4" style={{ backgroundColor: '#09090B', color: '#FFFFFF', fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 'bold' }}>
                  {loading ? 'Sending...' : 'Send OTP'}
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {mockOTP && (
                  <div className="p-3 border text-center" style={{ backgroundColor: '#FEF3C7', borderColor: '#F59E0B' }} data-testid="mock-otp-display">
                    <p className="text-xs" style={{ color: '#92400E', fontFamily: 'IBM Plex Sans, sans-serif' }}>Mock OTP (for testing)</p>
                    <p className="text-2xl font-black" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}>{mockOTP}</p>
                  </div>
                )}
                <div>
                  <Label htmlFor="reset-otp" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#09090B' }}>OTP</Label>
                  <Input id="reset-otp" type="text" placeholder="Enter OTP" value={resetOTP} onChange={(e) => setResetOTP(e.target.value)} data-testid="reset-otp-input" className="mt-2 p-4 border-2 focus:border-black" style={{ fontFamily: 'JetBrains Mono, monospace' }} />
                </div>
                <div>
                  <Label htmlFor="reset-new-password" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#09090B' }}>New Password</Label>
                  <Input id="reset-new-password" type="password" placeholder="Enter new password" value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} data-testid="reset-new-password-input" className="mt-2 p-4 border-2 focus:border-black" />
                </div>
                <Button onClick={handlePasswordResetConfirm} disabled={loading} data-testid="confirm-reset-btn" className="w-full p-4" style={{ backgroundColor: '#09090B', color: '#FFFFFF', fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 'bold' }}>
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </div>
            )}

            <button type="button" onClick={() => { setResetMode(false); setResetStep(1); setMockOTP(''); }} className="w-full text-center text-sm mt-4" style={{ color: '#71717A', fontFamily: 'IBM Plex Sans, sans-serif' }} data-testid="back-to-login-btn">
              Back to Login
            </button>
          </>
        ) : (
          <>
        <h1 
          className="text-2xl sm:text-3xl tracking-tight font-bold mb-2"
          style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}
          data-testid="auth-title"
        >
          {getPageTitle()}
        </h1>
        <p 
          className="text-xs tracking-[0.2em] uppercase font-bold mb-8"
          style={{ color: '#71717A', fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          {role.toUpperCase()}
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="phone" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#09090B' }}>
              Phone Number
            </Label>
            <Input
              id="phone"
              type="tel"
              placeholder="9876543210"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              required
              data-testid="phone-input"
              className="mt-2 p-4 border-2 focus:border-black"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            />
          </div>

          {role === 'visitor' && formData.phone && (
            <div>
              <Label htmlFor="otp" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#09090B' }}>
                OTP
              </Label>
              <Input
                id="otp"
                type="text"
                placeholder="1234"
                value={formData.otp}
                onChange={(e) => setFormData({ ...formData, otp: e.target.value })}
                data-testid="otp-input"
                className="mt-2 p-4 border-2 focus:border-black"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              />
            </div>
          )}

          {role !== 'visitor' && (
            <>
              {!isLogin && (
                <>
                  <div>
                    <Label htmlFor="name" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#09090B' }}>
                      Full Name
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="name-input"
                      className="mt-2 p-4 border-2 focus:border-black"
                    />
                  </div>

                  {role === 'owner' && (
                    <>
                      <div>
                        <Label htmlFor="flat_number" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#09090B' }}>
                          Flat Number
                        </Label>
                        <Input
                          id="flat_number"
                          type="text"
                          placeholder="A-101"
                          value={formData.flat_number}
                          onChange={(e) => setFormData({ ...formData, flat_number: e.target.value })}
                          data-testid="flat-input"
                          className="mt-2 p-4 border-2 focus:border-black"
                          style={{ fontFamily: 'JetBrains Mono, monospace' }}
                        />
                      </div>
                      
                      <div>
                        <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#09090B' }}>
                          Your Parking Slots (Max 5)
                        </Label>
                        <p className="text-xs mb-2" style={{ color: '#71717A', fontFamily: 'IBM Plex Sans, sans-serif' }}>
                          Add all parking slots you own
                        </p>
                        {formData.parking_slots.map((slot, index) => (
                          <div key={`slot-${index}-${formData.parking_slots.length}`} className="flex gap-2 mb-2">
                            <Input
                              type="text"
                              placeholder={`Slot ${index + 1} (e.g., A-101)`}
                              value={slot}
                              onChange={(e) => {
                                const newSlots = [...formData.parking_slots];
                                newSlots[index] = e.target.value;
                                setFormData({ ...formData, parking_slots: newSlots });
                              }}
                              data-testid={`parking-slot-${index}`}
                              className="p-3 border-2 focus:border-black flex-1"
                              style={{ fontFamily: 'JetBrains Mono, monospace' }}
                            />
                            {formData.parking_slots.length > 1 && (
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                  const newSlots = formData.parking_slots.filter((_, i) => i !== index);
                                  setFormData({ ...formData, parking_slots: newSlots });
                                }}
                                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                              >
                                Remove
                              </Button>
                            )}
                          </div>
                        ))}
                        {formData.parking_slots.length < 5 && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setFormData({ ...formData, parking_slots: [...formData.parking_slots, ''] });
                            }}
                            className="w-full mt-2"
                            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                            data-testid="add-slot-button"
                          >
                            + Add Another Slot
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}

              <div>
                <Label htmlFor="password" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#09090B' }}>
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="password-input"
                  className="mt-2 p-4 border-2 focus:border-black"
                />
              </div>
            </>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid="submit-button"
            className="w-full p-4 transition-transform hover:-translate-y-1"
            style={{
              backgroundColor: '#09090B',
              color: '#FFFFFF',
              fontFamily: 'Cabinet Grotesk, sans-serif',
              fontWeight: 'bold'
            }}
          >
            {getSubmitLabel()}
          </Button>

          {role !== 'visitor' && role !== 'owner' && role !== 'guard' && (
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="w-full text-center text-sm"
              style={{ color: '#71717A', fontFamily: 'IBM Plex Sans, sans-serif' }}
              data-testid="toggle-auth-mode"
            >
              {isLogin ? "Don't have an account? Register" : 'Already have an account? Login'}
            </button>
          )}

          {(role === 'owner') && isLogin && (
            <p className="text-center text-sm mt-2" style={{ color: '#71717A', fontFamily: 'IBM Plex Sans, sans-serif' }} data-testid="owner-register-hint">
              To register, use the registration link or QR code provided by your society admin.
            </p>
          )}

          {(role === 'guard') && isLogin && (
            <p className="text-center text-sm mt-2" style={{ color: '#71717A', fontFamily: 'IBM Plex Sans, sans-serif' }} data-testid="guard-register-hint">
              Guard accounts are created by the society admin.
            </p>
          )}

          {role !== 'visitor' && isLogin && (
            <button
              type="button"
              onClick={() => setResetMode(true)}
              className="w-full text-center text-sm mt-2"
              style={{ color: '#DC2626', fontFamily: 'IBM Plex Sans, sans-serif' }}
              data-testid="forgot-password-btn"
            >
              Forgot Password?
            </button>
          )}
        </form>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthPage;
